import { supabase } from './supabase';
import { BOMSummary } from './bom-calculator';

export interface SaveConfigurationParams {
  label: string;
  price: number;
  config: {
    model: string;
    height: number;
    width: number;
    depth: number;
    shelfCount: number;
    sectionCount: number;
    sides: 'single' | 'double';
    frameState: 'open' | 'closed' | 'outside';
  };
  bom: BOMSummary;
  useEdgeFunction?: boolean;
}

export interface SaveConfigurationResult {
  config_id: string;
  label: string;
  price: number;
}

function buildPayload(params: SaveConfigurationParams) {
  return {
    label: params.label,
    price: params.price,
    config: {
      ...params.config,
      bom_items: params.bom.items.map((item) => ({
        materialId: item.materialId,
        description: item.description,
        descriptionDE: item.descriptionDE,
        category: item.category,
        unit: item.unit,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
        weight: item.weight,
        totalWeight: item.totalWeight,
        ...(item.load !== undefined ? { load: item.load } : {}),
      })),
      total_weight: params.bom.totalWeight,
      total_load_capacity: params.bom.totalLoadCapacity,
    },
  };
}

async function saveViaEdgeFunction(
  payload: ReturnType<typeof buildPayload>
): Promise<{ data: SaveConfigurationResult | null; error: string | null }> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const url = `${supabaseUrl}/functions/v1/save-configuration`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${anonKey}`,
    },
    body: JSON.stringify(payload),
  });

  const json = await response.json();

  if (!response.ok) {
    return { data: null, error: json?.error ?? 'Failed to save configuration' };
  }

  return {
    data: {
      config_id: json.config_id,
      label: json.label,
      price: Number(json.price),
    },
    error: null,
  };
}

export async function saveConfiguration(
  params: SaveConfigurationParams
): Promise<{ data: SaveConfigurationResult | null; error: string | null }> {
  const payload = buildPayload(params);

  if (params.useEdgeFunction) {
    return saveViaEdgeFunction(payload);
  }

  const { data, error } = await supabase
    .from('saved_configurations')
    .insert(payload)
    .select('id, label, price')
    .single();

  if (error) {
    return { data: null, error: error.message };
  }

  return {
    data: {
      config_id: data.id,
      label: data.label,
      price: Number(data.price),
    },
    error: null,
  };
}
